import React, { useState, useRef } from 'react';
import { Order, OrderStatus, AISummary, PaymentStatus, PaymentRecord, OrderDocument, DEFAULT_CURRENCIES, OrderItem } from '../types';
import { StatusBadge } from './StatusBadge';
import { 
  ArrowLeft, Clock, Bot, Loader2, Anchor, FileText, User, Save, 
  Edit2, X, Trash2, Upload, Download, Paperclip, Check, DollarSign, 
  Plus, Calendar as CalendarIcon, ChevronDown, Building2, Mail, 
  Phone, MapPin, FileBox, FileArchive, Zap, Package, ShoppingCart
} from 'lucide-react';
import { analyzeOrder } from '../services/geminiService';
import { ConfirmationDialog } from './ConfirmationDialog';

interface RenderPartyInfoProps {
  label: string;
  name?: string;
  email?: string;
  phone?: string;
  address?: string;
}

const RenderPartyInfo: React.FC<RenderPartyInfoProps> = ({ label, name, email, phone, address }) => (
  <div className="bg-slate-50 p-4 rounded-2xl border border-slate-100 space-y-2">
    <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-1">{label}</label>
    <p className="font-black text-slate-800 uppercase tracking-tight text-sm">{name || 'Unspecified'}</p>
    <div className="space-y-1">
      {email && (
        <div className="flex items-center gap-2 text-[10px] text-slate-500 font-bold">
          <Mail size={12} className="text-indigo-400" /> {email}
        </div>
      )}
      {phone && (
        <div className="flex items-center gap-2 text-[10px] text-slate-500 font-bold">
          <Phone size={12} className="text-indigo-400" /> {phone}
        </div>
      )}
      {address && (
        <div className="flex items-start gap-2 text-[10px] text-slate-500 font-bold leading-tight pt-1">
          <MapPin size={12} className="text-indigo-400 mt-0.5 shrink-0" /> 
          <span className="whitespace-pre-wrap">{address}</span>
        </div>
      )}
    </div>
  </div>
);

interface OrderDetailProps {
  order: Order;
  onBack: () => void;
  onUpdateOrder: (order: Order) => void;
  onDeleteOrder?: (orderId: string) => void;
}

const DOCUMENT_TYPES = [
  "Invoice",
  "Bill of Lading (BOL)",
  "Packing List",
  "Certificate of Analysis",
  "Export Permit",
  "Customs Declaration",
  "Other"
];

export const OrderDetail: React.FC<OrderDetailProps> = ({ order, onBack, onUpdateOrder, onDeleteOrder }) => {
  const [analysis, setAnalysis] = useState<AISummary | null>(null);
  const [loadingAi, setLoadingAi] = useState(false);
  const [uploadType, setUploadType] = useState(DOCUMENT_TYPES[0]);
  
  // Cargo Editing
  const [isEditingItems, setIsEditingItems] = useState(false);
  const [editedItems, setEditedItems] = useState<OrderItem[]>([]);

  // Logistics Editing
  const [isEditingLogistics, setIsEditingLogistics] = useState(false);
  const [editedLogistics, setEditedLogistics] = useState({
    portOfDestination: '',
    vesselPlanned: '',
    ets: '',
    eta: '',
    blNumber: ''
  });

  // Financial Context Editing (Total Value/Currency)
  const [isEditingTotalValue, setIsEditingTotalValue] = useState(false);
  const [editedFinancials, setEditedFinancials] = useState({
    totalAmount: 0,
    currency: 'USD'
  });

  // Parties Editing
  const [isEditingParties, setIsEditingParties] = useState(false);
  const [editedParties, setEditedParties] = useState({
    supplier: '',
    supplierEmail: '',
    supplierPhone: '',
    supplierAddress: '',
    customerName: '',
    consigneeEmail: '',
    consigneePhone: '',
    consigneeAddress: '',
    shipper: '',
    shipperEmail: '',
    shipperPhone: '',
    shipperAddress: ''
  });

  // Payment Record Editing
  const [isAddingPayment, setIsAddingPayment] = useState(false);
  const [editingPaymentId, setEditingPaymentId] = useState<string | null>(null);
  const [paymentForm, setPaymentForm] = useState<Omit<PaymentRecord, 'id'>>({
    date: new Date().toISOString().split('T')[0],
    amount: 0,
    type: 'Part Payment',
    note: ''
  });

  // Confirmation Dialog State
  const [confirmConfig, setConfirmConfig] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: () => {},
    isDestructive: false
  });

  const closeConfirm = () => setConfirmConfig(prev => ({ ...prev, isOpen: false }));

  const handleAiAnalysis = async () => {
    setLoadingAi(true);
    try {
      const result = await analyzeOrder(order);
      setAnalysis(result);
    } catch (e) {
      console.error("AI Analysis failed", e);
    } finally {
      setLoadingAi(false);
    }
  };

  const handleStatusChange = (newStatus: OrderStatus) => {
    const updatedOrder: Order = {
        ...order,
        currentStatus: newStatus,
        timeline: [
            ...order.timeline,
            {
                status: newStatus,
                date: new Date().toISOString().split('T')[0],
                note: `Status updated to ${newStatus}`
            }
        ]
    };
    onUpdateOrder(updatedOrder);
  };

  const startEditingItems = () => {
    setEditedItems(order.items.map(i => ({ ...i })));
    setIsEditingItems(true);
  };

  const saveItems = () => {
    const totalAmount = editedItems.reduce((sum, item) => sum + (Number(item.quantity) * Number(item.unitPrice)), 0);
    const totalPaid = (order.financials.paymentHistory || []).reduce((sum, p) => sum + p.amount, 0);
    const balance = totalAmount - totalPaid;
    
    let status = PaymentStatus.PENDING;
    if (totalPaid >= totalAmount) status = PaymentStatus.PAID;
    else if (totalPaid > 0) status = PaymentStatus.PARTIAL;

    onUpdateOrder({
      ...order,
      items: editedItems,
      financials: {
        ...order.financials,
        totalAmount,
        amountPaid: totalPaid,
        balanceAmount: balance,
        paymentStatus: status
      }
    });
    setIsEditingItems(false);
  };

  const startEditingLogistics = () => {
    setEditedLogistics({
      portOfDestination: order.portOfDestination || '',
      vesselPlanned: order.vesselPlanned || '',
      ets: order.ets || '',
      eta: order.eta || '',
      blNumber: order.blNumber || ''
    });
    setIsEditingLogistics(true);
  };

  const saveLogistics = () => {
    onUpdateOrder({ ...order, ...editedLogistics });
    setIsEditingLogistics(false);
  };

  const startEditingTotalValue = () => {
    setEditedFinancials({
      totalAmount: order.financials.totalAmount,
      currency: order.financials.currency || 'USD'
    });
    setIsEditingTotalValue(true);
  };

  const saveTotalValue = () => {
    const totalPaid = (order.financials.paymentHistory || []).reduce((sum, p) => sum + p.amount, 0);
    const newBalance = editedFinancials.totalAmount - totalPaid;
    
    let status = PaymentStatus.PENDING;
    if (totalPaid >= editedFinancials.totalAmount) status = PaymentStatus.PAID;
    else if (totalPaid > 0) status = PaymentStatus.PARTIAL;

    onUpdateOrder({
      ...order,
      financials: {
        ...order.financials,
        totalAmount: editedFinancials.totalAmount,
        currency: editedFinancials.currency,
        balanceAmount: newBalance,
        paymentStatus: status
      }
    });
    setIsEditingTotalValue(false);
  };

  const startEditingParties = () => {
    setEditedParties({
      supplier: order.supplier,
      supplierEmail: order.supplierEmail || '',
      supplierPhone: order.supplierPhone || '',
      supplierAddress: order.supplierAddress || '',
      customerName: order.customerName,
      consigneeEmail: order.consigneeEmail || '',
      consigneePhone: order.consigneePhone || '',
      consigneeAddress: order.consigneeAddress || '',
      shipper: order.shipper || '',
      shipperEmail: order.shipperEmail || '',
      shipperPhone: order.shipperPhone || '',
      shipperAddress: order.shipperAddress || ''
    });
    setIsEditingParties(true);
  };

  const saveParties = () => {
    onUpdateOrder({ ...order, ...editedParties });
    setIsEditingParties(false);
  };

  const recalculateFinancials = (history: PaymentRecord[]) => {
    const totalPaid = history.reduce((sum, p) => sum + p.amount, 0);
    const balance = order.financials.totalAmount - totalPaid;
    
    let status = PaymentStatus.PENDING;
    if (totalPaid >= order.financials.totalAmount) status = PaymentStatus.PAID;
    else if (totalPaid > 0) status = PaymentStatus.PARTIAL;

    return {
      ...order.financials,
      amountPaid: totalPaid,
      balanceAmount: balance,
      paymentStatus: status,
      paymentHistory: history
    };
  };

  const handleSavePayment = () => {
    let newHistory = [...(order.financials.paymentHistory || [])];
    
    if (editingPaymentId) {
      newHistory = newHistory.map(p => p.id === editingPaymentId ? { ...paymentForm, id: p.id } : p);
    } else {
      newHistory.push({ ...paymentForm, id: `pay-${Date.now()}` });
    }

    onUpdateOrder({
      ...order,
      financials: recalculateFinancials(newHistory)
    });

    setIsAddingPayment(false);
    setEditingPaymentId(null);
  };

  const startEditPayment = (payment: PaymentRecord) => {
    setPaymentForm({
      date: payment.date,
      amount: payment.amount,
      type: payment.type,
      note: payment.note || ''
    });
    setEditingPaymentId(payment.id);
    setIsAddingPayment(true);
  };

  const handleDeletePayment = (paymentId: string) => {
    setConfirmConfig({
      isOpen: true,
      title: 'Delete Payment Record',
      message: 'Are you sure you want to remove this payment entry? This will update the order balance.',
      isDestructive: true,
      onConfirm: () => {
        const newHistory = (order.financials.paymentHistory || []).filter(p => p.id !== paymentId);
        onUpdateOrder({
          ...order,
          financials: recalculateFinancials(newHistory)
        });
        closeConfirm();
      }
    });
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const newDoc: OrderDocument = {
      id: `doc-${Date.now()}`,
      name: file.name,
      type: uploadType,
      date: new Date().toISOString().split('T')[0],
      size: `${(file.size / 1024).toFixed(1)} KB`,
      url: URL.createObjectURL(file) 
    };

    onUpdateOrder({
      ...order,
      documents: [...(order.documents || []), newDoc]
    });
  };

  const removeDocument = (docId: string) => {
    if (!window.confirm("Archive this document from the registry?")) return;
    onUpdateOrder({
      ...order,
      documents: (order.documents || []).filter(d => d.id !== docId)
    });
  };

  return (
    <div className="space-y-8 animate-in fade-in duration-500 pb-20">
      <ConfirmationDialog 
        isOpen={confirmConfig.isOpen}
        title={confirmConfig.title}
        message={confirmConfig.message}
        onConfirm={confirmConfig.onConfirm}
        onCancel={closeConfirm}
        isDestructive={confirmConfig.isDestructive}
      />

      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-4">
          <button onClick={onBack} className="p-2 hover:bg-slate-100 rounded-full transition-colors text-slate-500" title="Go back">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <div className="flex items-center gap-3">
              <h2 className="text-2xl font-black text-slate-900">Order {order.id}</h2>
              <StatusBadge status={order.currentStatus} />
            </div>
            <p className="text-slate-500 text-sm font-medium">Registry Created: {order.orderDate}</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <div className="bg-white border border-slate-200 rounded-xl p-1.5 flex items-center shadow-sm">
             <span className="text-[9px] font-black text-slate-400 uppercase tracking-widest px-3">Mark As:</span>
             <div className="flex gap-1 overflow-x-auto no-scrollbar max-w-[400px]">
                {Object.values(OrderStatus).map((status) => (
                    <button
                        key={status}
                        onClick={() => handleStatusChange(status)}
                        className={`px-3 py-1.5 rounded-lg text-[9px] font-black uppercase tracking-widest transition-all whitespace-nowrap ${order.currentStatus === status ? 'bg-indigo-600 text-white shadow-md' : 'text-slate-500 hover:bg-slate-100'}`}
                    >
                        {status}
                    </button>
                ))}
             </div>
          </div>
          <button 
            onClick={handleAiAnalysis} 
            disabled={loadingAi}
            className="flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg hover:bg-slate-800 transition-all disabled:opacity-50"
          >
            {loadingAi ? <Loader2 size={18} className="animate-spin" /> : <Bot size={18} />}
            Risk Assessment
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-8">
          
          {/* Itemized Cargo Section */}
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <Package className="text-indigo-500" size={20} /> Itemized Cargo
                </h3>
                <p className="text-xs text-slate-500 font-medium mt-1">Detailed breakdown of products and quantities in this consignment.</p>
              </div>
              {!isEditingItems ? (
                <button 
                  onClick={startEditingItems} 
                  className="flex items-center gap-2 px-4 py-2 text-[10px] font-black uppercase tracking-widest text-indigo-600 bg-indigo-50 border border-indigo-100 rounded-xl hover:bg-indigo-100 transition-all"
                >
                  <Edit2 size={14}/> Edit Cargo
                </button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => setIsEditingItems(false)} className="flex items-center gap-1.5 px-4 py-2 text-[10px] font-black uppercase text-slate-500 hover:bg-slate-100 rounded-xl transition-all"><X size={14}/> Discard</button>
                  <button onClick={saveItems} className="flex items-center gap-1.5 px-4 py-2 text-[10px] font-black uppercase bg-indigo-600 text-white rounded-xl shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-all"><Save size={14}/> Save Changes</button>
                </div>
              )}
            </div>

            {isEditingItems ? (
              <div className="space-y-4">
                {editedItems.map((item, idx) => (
                  <div key={item.id} className="grid grid-cols-12 gap-3 items-end p-5 bg-slate-50 rounded-2xl border border-slate-100 group relative overflow-hidden">
                    <div className="absolute top-0 left-0 w-1 h-full bg-indigo-500 opacity-20"></div>
                    <div className="col-span-12 md:col-span-5">
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1.5 ml-1">Product Description</label>
                      <input 
                        type="text" 
                        value={item.name} 
                        onChange={e => {
                          const next = [...editedItems];
                          next[idx].name = e.target.value.toUpperCase();
                          setEditedItems(next);
                        }}
                        className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-black uppercase focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all"
                        placeholder="E.G. BITUMEN 60/70"
                      />
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <label className="text-[9px] font-black text-indigo-500 uppercase tracking-widest block mb-1.5 ml-1">Quantity (MT)</label>
                      <input 
                        type="number" 
                        step="0.001"
                        value={item.quantity === 0 ? '' : item.quantity} 
                        onChange={e => {
                          const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                          const next = [...editedItems];
                          next[idx].quantity = isNaN(val) ? 0 : val;
                          setEditedItems(next);
                        }}
                        className="w-full px-3 py-2.5 bg-white border border-indigo-200 rounded-xl text-xs font-black text-indigo-600 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all"
                        placeholder="0.000"
                      />
                    </div>
                    <div className="col-span-6 md:col-span-3">
                      <label className="text-[9px] font-black text-slate-400 uppercase tracking-widest block mb-1.5 ml-1">Unit Price ($)</label>
                      <input 
                        type="number" 
                        step="0.01"
                        value={item.unitPrice === 0 ? '' : item.unitPrice} 
                        onChange={e => {
                          const val = e.target.value === '' ? 0 : parseFloat(e.target.value);
                          const next = [...editedItems];
                          next[idx].unitPrice = isNaN(val) ? 0 : val;
                          setEditedItems(next);
                        }}
                        className="w-full px-3 py-2.5 bg-white border border-slate-200 rounded-xl text-xs font-black text-slate-700 focus:ring-4 focus:ring-indigo-500/10 outline-none transition-all"
                        placeholder="0.00"
                      />
                    </div>
                    <div className="col-span-12 md:col-span-1 flex justify-center pb-2">
                       <button 
                        onClick={() => setEditedItems(editedItems.filter((_, i) => i !== idx))}
                        className="p-2 text-slate-300 hover:text-rose-500 hover:bg-rose-50 rounded-xl transition-all"
                        title="Remove Line"
                       >
                         <Trash2 size={16} />
                       </button>
                    </div>
                  </div>
                ))}
                <button 
                  onClick={() => setEditedItems([...editedItems, { id: Date.now().toString(), name: '', quantity: 0, unitPrice: 0 }])}
                  className="w-full py-4 border-2 border-dashed border-slate-200 rounded-3xl text-slate-400 hover:text-indigo-600 hover:border-indigo-300 transition-all flex items-center justify-center gap-2 text-[10px] font-black uppercase tracking-[0.2em] bg-slate-50/30"
                >
                  <Plus size={16} /> Append Cargo Line
                </button>
              </div>
            ) : (
              <div className="space-y-4">
                {order.items && order.items.length > 0 ? (
                  order.items.map(item => (
                    <div key={item.id} className="flex items-center justify-between p-5 bg-slate-50 border border-slate-100 rounded-2xl group hover:border-indigo-200 transition-all">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center border border-slate-100 shadow-sm text-slate-400 group-hover:text-indigo-500 transition-colors">
                          <Package size={20} />
                        </div>
                        <div>
                          <p className="text-sm font-black text-slate-900 uppercase tracking-tight">{item.name}</p>
                          <p className="text-[10px] text-slate-400 font-bold mt-0.5">Valuation: ${item.unitPrice.toLocaleString()} / MT</p>
                        </div>
                      </div>
                      <div className="text-right">
                         <p className="text-sm font-black text-slate-800">{item.quantity.toLocaleString()} MT</p>
                         <p className="text-[10px] font-black text-indigo-600 uppercase tracking-widest mt-0.5">${(item.quantity * item.unitPrice).toLocaleString()}</p>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="py-12 text-center text-slate-400 italic text-sm border-2 border-dashed border-slate-100 rounded-3xl">
                    No items listed in this consignment.
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Financials Section */}
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                  <DollarSign className="text-indigo-500" size={20} /> Financial Ledger
                </h3>
                <p className="text-xs text-slate-500 font-medium mt-1">Transaction history for contract value and disbursed funds.</p>
              </div>
              {!isAddingPayment && (
                <button 
                  onClick={() => {
                    setIsAddingPayment(true);
                    setEditingPaymentId(null);
                    setPaymentForm({
                      date: new Date().toISOString().split('T')[0],
                      amount: 0,
                      type: 'Part Payment',
                      note: ''
                    });
                  }}
                  className="bg-indigo-600 text-white px-4 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider flex items-center gap-2 hover:bg-indigo-700 transition-all shadow-lg shadow-indigo-100"
                >
                  <Plus size={16} /> New Payment
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-slate-50 p-5 rounded-2xl border border-slate-100 relative group">
                <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-1">Contract Total</p>
                {isEditingTotalValue ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                        <select 
                            value={editedFinancials.currency}
                            onChange={e => setEditedFinancials({...editedFinancials, currency: e.target.value})}
                            className="bg-white border border-indigo-200 rounded p-1 text-xs font-bold text-slate-700 outline-none"
                        >
                            {DEFAULT_CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <input 
                            type="number" 
                            value={editedFinancials.totalAmount}
                            onChange={e => setEditedFinancials({...editedFinancials, totalAmount: parseFloat(e.target.value) || 0})}
                            className="w-full bg-white border border-indigo-200 rounded p-1 text-sm font-black text-slate-900 outline-none"
                        />
                    </div>
                    <div className="flex justify-end gap-2">
                        <button onClick={saveTotalValue} className="p-1.5 bg-emerald-500 text-white rounded hover:bg-emerald-600 transition-all shadow-sm">
                            <Check size={14}/>
                        </button>
                        <button onClick={() => setIsEditingTotalValue(false)} className="p-1.5 bg-slate-200 text-slate-600 rounded hover:bg-slate-300 transition-all">
                            <X size={14}/>
                        </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <p className="text-xl font-black text-slate-900">{order.financials.currency || 'USD'} {order.financials.totalAmount.toLocaleString()}</p>
                    <button onClick={startEditingTotalValue} className="opacity-0 group-hover:opacity-100 p-1 text-slate-400 hover:text-indigo-600 transition-all">
                      <Edit2 size={14} />
                    </button>
                  </div>
                )}
              </div>
              <div className="bg-emerald-50 p-5 rounded-2xl border border-emerald-100">
                <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest mb-1">Aggregate Paid</p>
                <p className="text-xl font-black text-emerald-700">{order.financials.currency || 'USD'} {order.financials.amountPaid.toLocaleString()}</p>
              </div>
              <div className={`p-5 rounded-2xl border transition-colors ${order.financials.balanceAmount <= 0 ? 'bg-indigo-50 border-indigo-100' : 'bg-rose-50 border-rose-100'}`}>
                <p className={`text-[10px] font-black uppercase tracking-widest mb-1 ${order.financials.balanceAmount <= 0 ? 'text-indigo-600' : 'text-rose-600'}`}>
                  {order.financials.balanceAmount <= 0 ? 'Fully Settled' : 'Balance Payable'}
                </p>
                <p className={`text-xl font-black ${order.financials.balanceAmount <= 0 ? 'text-indigo-700' : 'text-rose-700'}`}>
                  {order.financials.currency || 'USD'} {order.financials.balanceAmount.toLocaleString()}
                </p>
              </div>
            </div>

            {isAddingPayment && (
              <div className="mb-8 p-6 bg-indigo-50/50 border border-indigo-100 rounded-3xl animate-in zoom-in-95 duration-200">
                <div className="flex justify-between items-center mb-6">
                  <h4 className="font-bold text-indigo-900 uppercase tracking-widest text-[10px]">{editingPaymentId ? 'Modify Payment Entry' : 'Register New Payment'}</h4>
                  <button onClick={() => setIsAddingPayment(false)} className="text-slate-400 hover:text-rose-600"><X size={18} /></button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase block mb-1">Date</label>
                    <input type="date" value={paymentForm.date} onChange={e => setPaymentForm({...paymentForm, date: e.target.value})} className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-sm" />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase block mb-1">Amount ({order.financials.currency})</label>
                    <input type="number" value={paymentForm.amount || ''} onChange={e => setPaymentForm({...paymentForm, amount: parseFloat(e.target.value) || 0})} className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold" placeholder="0.00" />
                  </div>
                  <div>
                    <label className="text-[10px] font-black text-slate-400 uppercase block mb-1">Type</label>
                    <select value={paymentForm.type} onChange={e => setPaymentForm({...paymentForm, type: e.target.value as any})} className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-sm font-bold text-slate-700">
                      <option value="Advance">Advance</option>
                      <option value="Part Payment">Part Payment</option>
                      <option value="Balance">Balance</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <button onClick={handleSavePayment} className="w-full py-2.5 bg-indigo-600 text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-lg hover:bg-indigo-700 transition-all flex items-center justify-center gap-2">
                      <Check size={16} /> {editingPaymentId ? 'Commit' : 'Post'}
                    </button>
                  </div>
                  <div className="md:col-span-4">
                    <label className="text-[10px] font-black text-slate-400 uppercase block mb-1">Payment Reference / Note</label>
                    <input type="text" value={paymentForm.note} onChange={e => setPaymentForm({...paymentForm, note: e.target.value})} className="w-full p-2.5 bg-white border border-slate-200 rounded-xl text-sm" placeholder="e.g. Wire Ref #99201, Bank of Africa" />
                  </div>
                </div>
              </div>
            )}

            <div className="space-y-3">
              {order.financials.paymentHistory && order.financials.paymentHistory.length > 0 ? (
                order.financials.paymentHistory.map((payment) => (
                  <div key={payment.id} className="flex items-center justify-between p-5 bg-slate-50 border border-slate-100 rounded-2xl group hover:border-indigo-200 transition-all">
                    <div className="flex items-center gap-4">
                      <div className="w-12 h-12 bg-white rounded-2xl flex items-center justify-center border border-slate-100 shadow-sm text-indigo-600">
                        <DollarSign size={20} />
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="text-sm font-black text-slate-900">{order.financials.currency || 'USD'} {payment.amount.toLocaleString()}</p>
                          <span className="text-[9px] font-black uppercase bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-lg border border-indigo-200">{payment.type}</span>
                        </div>
                        <p className="text-[10px] text-slate-400 font-bold mt-1 flex items-center gap-1">
                          <CalendarIcon size={10} /> {payment.date} {payment.note && ` • ${payment.note}`}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                      <button onClick={() => startEditPayment(payment)} className="p-2 text-slate-400 hover:text-indigo-600 hover:bg-white rounded-xl transition-all">
                        <Edit2 size={16} />
                      </button>
                      <button onClick={() => handleDeletePayment(payment.id)} className="p-2 text-slate-400 hover:text-rose-600 hover:bg-white rounded-xl transition-all">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </div>
                ))
              ) : (
                <div className="py-12 text-center text-slate-400 italic text-sm border-2 border-dashed border-slate-100 rounded-3xl">
                  No payment records found for this order.
                </div>
              )}
            </div>
          </div>

          {/* Logistics Card */}
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                <Anchor className="text-slate-400" size={20} /> Shipping Intelligence
              </h3>
              {!isEditingLogistics ? (
                <button onClick={() => startEditingLogistics()} className="p-2 text-slate-400 hover:text-indigo-600 transition-colors"><Edit2 size={18}/></button>
              ) : (
                <div className="flex gap-2">
                  <button onClick={() => setIsEditingLogistics(false)} className="p-2 text-slate-400 hover:text-rose-600"><X size={20}/></button>
                  <button onClick={saveLogistics} className="p-2 text-emerald-600"><Save size={20}/></button>
                </div>
              )}
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Destination Port</label>
                {isEditingLogistics ? (
                  <input type="text" value={editedLogistics.portOfDestination} onChange={e => setEditedLogistics({...editedLogistics, portOfDestination: e.target.value})} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm" />
                ) : (
                  <p className="font-bold text-slate-800">{order.portOfDestination}</p>
                )}
              </div>
              <div>
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest block mb-2">Vessel / Voyage</label>
                {isEditingLogistics ? (
                  <input type="text" value={editedLogistics.vesselPlanned} onChange={e => setEditedLogistics({...editedLogistics, vesselPlanned: e.target.value})} className="w-full p-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm" />
                ) : (
                  <p className="font-bold text-slate-800">{order.vesselPlanned || 'TBD'}</p>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Counterparties & Docs */}
        <div className="space-y-8">
          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
            <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
                    <User className="text-slate-400" size={20} /> Operational Parties
                </h3>
                {!isEditingParties ? (
                    <button onClick={startEditingParties} className="p-2 text-slate-400 hover:text-indigo-600 transition-colors">
                        <Edit2 size={18} />
                    </button>
                ) : (
                    <div className="flex gap-2">
                        <button onClick={() => setIsEditingParties(false)} className="p-1.5 text-rose-500 hover:bg-rose-50 rounded-lg">
                            <X size={18} />
                        </button>
                        <button onClick={saveParties} className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg">
                            <Check size={18} />
                        </button>
                    </div>
                )}
            </div>
            
            <div className="space-y-6">
              {isEditingParties ? (
                <div className="space-y-8 max-h-[60vh] overflow-y-auto pr-2 custom-scrollbar">
                   {/* Consignee Edit */}
                   <div className="space-y-3 p-4 bg-slate-50 rounded-2xl border border-slate-200">
                      <label className="text-[10px] font-black text-indigo-600 uppercase tracking-widest block">Consignee</label>
                      <input 
                          type="text" 
                          value={editedParties.customerName} 
                          onChange={e => setEditedParties({...editedParties, customerName: e.target.value})}
                          className="w-full p-2 bg-white border border-slate-200 rounded-lg text-sm font-bold"
                          placeholder="Name"
                      />
                      <input 
                          type="email" 
                          value={editedParties.consigneeEmail} 
                          onChange={e => setEditedParties({...editedParties, consigneeEmail: e.target.value})}
                          className="w-full p-2 bg-white border border-slate-200 rounded-lg text-xs"
                          placeholder="Email"
                      />
                      <input 
                          type="tel" 
                          value={editedParties.consigneePhone} 
                          onChange={e => setEditedParties({...editedParties, consigneePhone: e.target.value})}
                          className="w-full p-2 bg-white border border-slate-200 rounded-lg text-xs"
                          placeholder="Phone"
                      />
                      <textarea 
                          value={editedParties.consigneeAddress} 
                          onChange={e => setEditedParties({...editedParties, consigneeAddress: e.target.value})}
                          className="w-full p-2 bg-white border border-slate-200 rounded-lg text-xs h-20 resize-none"
                          placeholder="Address"
                      />
                   </div>

                   {/* Supplier Edit */}
                   <div className="space-y-3 p-4 bg-slate-50 rounded-2xl border border-slate-200">
                      <label className="text-[10px] font-black text-indigo-600 uppercase tracking-widest block">Supplier</label>
                      <input 
                          type="text" 
                          value={editedParties.supplier} 
                          onChange={e => setEditedParties({...editedParties, supplier: e.target.value})}
                          className="w-full p-2 bg-white border border-slate-200 rounded-lg text-sm font-bold"
                          placeholder="Name"
                      />
                      <input 
                          type="email" 
                          value={editedParties.supplierEmail} 
                          onChange={e => setEditedParties({...editedParties, supplierEmail: e.target.value})}
                          className="w-full p-2 bg-white border border-slate-200 rounded-lg text-xs"
                          placeholder="Email"
                      />
                      <input 
                          type="tel" 
                          value={editedParties.supplierPhone} 
                          onChange={e => setEditedParties({...editedParties, supplierPhone: e.target.value})}
                          className="w-full p-2 bg-white border border-slate-200 rounded-lg text-xs"
                          placeholder="Phone"
                      />
                      <textarea 
                          value={editedParties.supplierAddress} 
                          onChange={e => setEditedParties({...editedParties, supplierAddress: e.target.value})}
                          className="w-full p-2 bg-white border border-slate-200 rounded-lg text-xs h-20 resize-none"
                          placeholder="Address"
                      />
                   </div>

                   {/* Shipper Edit */}
                   <div className="space-y-3 p-4 bg-slate-50 rounded-2xl border border-slate-200">
                      <label className="text-[10px] font-black text-indigo-600 uppercase tracking-widest block">Shipper / Agent</label>
                      <input 
                          type="text" 
                          value={editedParties.shipper} 
                          onChange={e => setEditedParties({...editedParties, shipper: e.target.value})}
                          className="w-full p-2 bg-white border border-slate-200 rounded-lg text-sm font-bold"
                          placeholder="Name"
                      />
                      <input 
                          type="email" 
                          value={editedParties.shipperEmail} 
                          onChange={e => setEditedParties({...editedParties, shipperEmail: e.target.value})}
                          className="w-full p-2 bg-white border border-slate-200 rounded-lg text-xs"
                          placeholder="Email"
                      />
                      <input 
                          type="tel" 
                          value={editedParties.shipperPhone} 
                          onChange={e => setEditedParties({...editedParties, shipperPhone: e.target.value})}
                          className="w-full p-2 bg-white border border-slate-200 rounded-lg text-xs"
                          placeholder="Phone"
                      />
                      <textarea 
                          value={editedParties.shipperAddress} 
                          onChange={e => setEditedParties({...editedParties, shipperAddress: e.target.value})}
                          className="w-full p-2 bg-white border border-slate-200 rounded-lg text-xs h-20 resize-none"
                          placeholder="Address"
                      />
                   </div>
                </div>
              ) : (
                <div className="space-y-4">
                  <RenderPartyInfo 
                    label="Consignee" 
                    name={order.customerName}
                    email={order.consigneeEmail}
                    phone={order.consigneePhone}
                    address={order.consigneeAddress}
                  />
                  <RenderPartyInfo 
                    label="Supplier" 
                    name={order.supplier}
                    email={order.supplierEmail}
                    phone={order.supplierPhone}
                    address={order.supplierAddress}
                  />
                  <RenderPartyInfo 
                    label="Shipper / Logistics" 
                    name={order.shipper}
                    email={order.shipperEmail}
                    phone={order.shipperPhone}
                    address={order.shipperAddress}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="bg-slate-900 rounded-[32px] p-8 text-white shadow-xl">
             <div className="mb-6">
                <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-bold flex items-center gap-2">
                        <FileText className="text-indigo-400" size={20} /> Documents
                    </h3>
                    <div className="w-10 h-1 bg-white/10 rounded-full"></div>
                </div>
                
                {/* Upload Console */}
                <div className="bg-white/5 border border-white/10 rounded-2xl p-4 space-y-4">
                    <div className="space-y-1.5">
                        <label className="text-[9px] font-black text-slate-500 uppercase tracking-widest block ml-1">Classification</label>
                        <select 
                            value={uploadType}
                            onChange={(e) => setUploadType(e.target.value)}
                            className="w-full bg-slate-800 border border-white/10 rounded-xl px-3 py-2 text-xs font-bold text-white outline-none focus:ring-2 focus:ring-indigo-500 transition-all"
                        >
                            {DOCUMENT_TYPES.map(type => (
                                <option key={type} value={type}>{type}</option>
                            ))}
                        </select>
                    </div>
                    <label className="w-full flex items-center justify-center gap-2 py-3 bg-indigo-600 hover:bg-indigo-500 text-white rounded-xl font-black text-[10px] uppercase tracking-widest cursor-pointer transition-all active:scale-95 shadow-lg shadow-indigo-900/40">
                        <Upload size={14} /> Attach {uploadType.split(' (')[0]}
                        <input type="file" className="hidden" onChange={handleFileUpload} />
                    </label>
                </div>
             </div>

             <div className="space-y-3 max-h-80 overflow-y-auto custom-scrollbar pr-2">
                {order.documents && order.documents.length > 0 ? order.documents.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-4 bg-white/5 rounded-2xl border border-white/10 group hover:bg-white/10 transition-all">
                    <div className="flex items-center gap-3 min-w-0">
                      <div className="w-10 h-10 bg-white/5 rounded-xl flex items-center justify-center border border-white/5 text-slate-400 group-hover:text-indigo-400 transition-colors">
                        {doc.type.includes('BOL') ? <Anchor size={18}/> : <FileText size={18}/>}
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-black truncate text-white" title={doc.name}>{doc.name}</p>
                        <div className="flex items-center gap-2 mt-1">
                            <span className="text-[8px] font-black uppercase px-1.5 py-0.5 bg-indigo-500/20 text-indigo-300 rounded border border-indigo-500/30">{doc.type}</span>
                            <span className="text-[8px] text-slate-500 font-bold">{doc.size}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                      <a href={doc.url} download={doc.name} className="p-2 text-slate-400 hover:text-white bg-white/5 rounded-lg" title="Download">
                        <Download size={14} />
                      </a>
                      <button onClick={() => removeDocument(doc.id)} className="p-2 text-slate-400 hover:text-rose-400 bg-white/5 rounded-lg" title="Archive">
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                )) : (
                  <div className="py-12 flex flex-col items-center justify-center text-slate-600 border border-dashed border-white/10 rounded-2xl">
                      <FileArchive size={32} className="opacity-20 mb-3" />
                      <p className="text-[10px] font-black uppercase tracking-widest">No Documents Attached</p>
                  </div>
                )}
             </div>
          </div>
        </div>
      </div>
    </div>
  );
};